import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import apiReports, {
  IOpenWorkTypeOffer,
  IOpenWorkTypePosition,
  IOpenWorkTypes,
} from 'api/reports';
import PageContainer from 'components/layout/PageContainer';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import getOfferStatusLabel from 'services/offers/offerStatusLabel';
import translations from 'services/translations/translations';

import { useReportCurrency } from 'services/offers/exchangeRate';
import CurrencyToggle from 'components/offers/src/CurrencyToggle';

import { BarList, Legend, PALETTE } from './DashboardCharts';

import 'style/reports/OfferDashboard.css';
import 'style/reports/OpenWorkTypes.css';

// The preliminary offers and offers, together or one of them
const TYPE_FILTERS = ['all', 'preliminary-offer', 'offer'];

// A key of the department of an offer without one
const NO_DEPARTMENT = '';

// The periods: the offers whose latest revision was made during the last months, or all
const PERIOD_MONTHS = [3, 6, 12, 24];
const DEFAULT_MONTHS = 12;

// The units shown in a cell; the positions show all
const MAX_UNITS = 3;

// The positions shown before "Show all"
const POSITIONS_SHOWN = 100;

// The positions of the same name: the bars of the chart and the rows before "Show all"
const NAMES_CHARTED = 15;
const NAMES_SHOWN = 30;

// Positions of the same name are summed: the case and extra spaces do not matter
const nameKey = (name: null | string): string =>
  (name ?? '').trim().replace(/\s+/g, ' ').toLocaleLowerCase('fi');

interface INameSummary {
  byDepartment: Map<string, ICell>;
  key: string;
  // The name as it was first written
  label: string;
  total: ICell;
}

interface ICell {
  // The offers with positions of the work type in the department
  offerIds: Set<number>;
  // The quantities by unit, e.g. m2: 1200
  quantities: { [unit: string]: number };
  value: number;
}

const emptyCell = (): ICell => ({
  offerIds: new Set<number>(),
  quantities: {},
  value: 0,
});

const addTo = (cell: ICell, position: IOpenWorkTypePosition) => {
  cell.offerIds.add(position.offerId);
  const unit = position.unit ?? '';
  // eslint-disable-next-line no-param-reassign
  cell.quantities[unit] = (cell.quantities[unit] ?? 0) + position.quantity;
  // eslint-disable-next-line no-param-reassign
  cell.value += position.value;
};

/**
 * The open preliminary offers and offers (open, ready to send or sent) by work type: the
 * sales, the number of offers and the quantities of each work type by department and in
 * total, and their positions. A cell or a row chooses the positions shown below.
 */
const OpenWorkTypes = ({ tabs }: { tabs?: JSX.Element }): JSX.Element => {
  const history = useHistory();
  const { t } = useTranslation([
    translations.offers,
    translations.products,
    translations.common,
  ]);
  const tr = (key: string, options?: Record<string, unknown>) =>
    t(`${translations.offers}:dashboard.openWorkTypes.${key}`, options);

  const [data, setData] = useState<IOpenWorkTypes | null>(null);
  const [failed, setFailed] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  // null: all the open offers
  const [months, setMonths] = useState<null | number>(DEFAULT_MONTHS);
  // The work type and department of the positions shown, null for all
  const [group, setGroup] = useState<null | string>(null);
  const [department, setDepartment] = useState<null | string>(null);
  const [showAll, setShowAll] = useState(false);
  // The positions of one name shown below, null for all
  const [positionName, setPositionName] = useState<null | string>(null);
  const [showAllNames, setShowAllNames] = useState(false);

  // Loaded again for another period; an earlier answer arriving late is ignored
  useEffect(() => {
    let active = true;
    setData(null);
    setFailed(false);
    apiReports
      .getOpenWorkTypes(months)
      .then((loaded) => {
        if (active) {
          setData(loaded);
        }
      })
      .catch(() => {
        if (active) {
          setFailed(true);
        }
      });

    return () => {
      active = false;
    };
  }, [months]);

  const groupLabel = (key: string) =>
    key ? t(`${translations.products}:groups.${key}`) : tr('No work type');
  const unitLabel = (unit: string) =>
    unit ? t(`${translations.common}:units.${unit}`) : '';
  // The largest quantities by unit, e.g. "2 908 m² · 35 636 kpl", the rest as "…"
  const quantitiesText = (quantities: { [unit: string]: number }) => {
    const entries = Object.entries(quantities)
      .filter(([, quantity]) => quantity > 0)
      .sort(([, a], [, b]) => b - a);
    const shown = entries
      .slice(0, MAX_UNITS)
      .map(([unit, quantity]) =>
        `${formatGroupedNumber(quantity, 1, 0)} ${unitLabel(unit)}`.trim()
      );

    return [...shown, ...(entries.length > MAX_UNITS ? ['…'] : [])].join(' · ');
  };

  const offersById = useMemo(() => {
    const map = new Map<number, IOpenWorkTypeOffer>();
    (data?.offers ?? []).forEach((offer) => map.set(offer.id, offer));
    return map;
  }, [data]);

  const departmentOf = (offer?: IOpenWorkTypeOffer) =>
    offer?.departmentName ?? NO_DEPARTMENT;

  // The sums in one currency: euros, or kronas when only Swedish offers are viewed (e.g.
  // a Swedish department chosen); the other at the rate of today
  const viewedOffers = (data?.offers ?? []).filter(
    (offer) =>
      (typeFilter === 'all' || offer.type === typeFilter) &&
      (department === null || departmentOf(offer) === department)
  );
  const report = useReportCurrency(viewedOffers.map((offer) => offer.country));
  const euros = (value: number): string =>
    `${formatGroupedNumber(value, 0, 0)} ${report.symbol}`;

  // The positions of the offers of the chosen type, their sums in the currency shown
  const positions = useMemo(
    () =>
      (data?.positions ?? [])
        .filter((position) => {
          const offer = offersById.get(position.offerId);
          return (
            offer !== undefined &&
            (typeFilter === 'all' || offer.type === typeFilter)
          );
        })
        .map((position) => {
          const country = offersById.get(position.offerId)?.country;
          return {
            ...position,
            expense: report.convertFrom(country, position.expense),
            value: report.convertFrom(country, position.value),
          };
        }),
    [data, offersById, typeFilter, report.currency, report.rate]
  );

  const departments = useMemo(
    () =>
      Array.from(
        new Set(
          (data?.offers ?? [])
            .filter(
              (offer) => typeFilter === 'all' || offer.type === typeFilter
            )
            .map((offer) => departmentOf(offer))
        )
      ).sort((a, b) => a.localeCompare(b, 'fi')),
    [data, typeFilter]
  );

  // The cells by work type and department, the totals of each work type and department
  const { cells, groupTotals, departmentTotals, total } = useMemo(() => {
    const byCell = new Map<string, ICell>();
    const byGroup = new Map<string, ICell>();
    const byDepartment = new Map<string, ICell>();
    const all = emptyCell();

    positions.forEach((position) => {
      const departmentKey = departmentOf(offersById.get(position.offerId));
      const cellKey = `${position.group}|${departmentKey}`;
      [
        [byCell, cellKey],
        [byGroup, position.group],
        [byDepartment, departmentKey],
      ].forEach(([map, key]) => {
        const cellMap = map as Map<string, ICell>;
        const cell = cellMap.get(key as string) ?? emptyCell();
        addTo(cell, position);
        cellMap.set(key as string, cell);
      });
      addTo(all, position);
    });

    return {
      cells: byCell,
      departmentTotals: byDepartment,
      groupTotals: byGroup,
      total: all,
    };
  }, [positions, offersById]);

  // The work types with the largest sales first
  const groups = Array.from(groupTotals.entries())
    .sort(([, a], [, b]) => b.value - a.value)
    .map(([key]) => key);

  const choose = (nextGroup: null | string, nextDepartment: null | string) => {
    const same = nextGroup === group && nextDepartment === department;
    setGroup(same ? null : nextGroup);
    setDepartment(same ? null : nextDepartment);
    setPositionName(null);
    setShowAll(false);
  };

  // The positions of the chosen work type and department
  const positionsOfChoice = positions.filter(
    (position) =>
      (group === null || position.group === group) &&
      (department === null ||
        departmentOf(offersById.get(position.offerId)) === department)
  );

  // The positions of the same name summed by department, the largest sales first
  const names: INameSummary[] = (() => {
    const byName = new Map<string, INameSummary>();
    positionsOfChoice.forEach((position) => {
      const key = nameKey(position.name);
      const summary = byName.get(key) ?? {
        byDepartment: new Map<string, ICell>(),
        key,
        label: (position.name ?? '').trim() || '–',
        total: emptyCell(),
      };
      const departmentKey = departmentOf(offersById.get(position.offerId));
      const cell = summary.byDepartment.get(departmentKey) ?? emptyCell();
      addTo(cell, position);
      summary.byDepartment.set(departmentKey, cell);
      addTo(summary.total, position);
      byName.set(key, summary);
    });

    return Array.from(byName.values()).sort(
      (a, b) => b.total.value - a.total.value
    );
  })();
  const shownNames = showAllNames ? names : names.slice(0, NAMES_SHOWN);
  // The departments in the summary, each with its color in the chart
  const nameDepartments = departments.filter((key) =>
    names.some((summary) => summary.byDepartment.has(key))
  );
  const departmentColor = (key: string) =>
    PALETTE[Math.max(0, departments.indexOf(key)) % PALETTE.length];
  const departmentLabel = (key: string) => key || tr('No department');

  const chooseName = (key: string) => {
    setPositionName(positionName === key ? null : key);
    setShowAll(false);
  };

  // The chosen positions, of the largest work type first; the first ones until "Show all"
  const chosenPositions = positionsOfChoice
    .filter(
      (position) =>
        positionName === null || nameKey(position.name) === positionName
    )
    .sort(
      (a, b) =>
        groups.indexOf(a.group) - groups.indexOf(b.group) || b.value - a.value
    );
  const shownPositions = showAll
    ? chosenPositions
    : chosenPositions.slice(0, POSITIONS_SHOWN);

  const cellContent = (cell: ICell | undefined) =>
    cell === undefined ? (
      <span className="open-work-types-empty">–</span>
    ) : (
      <>
        <span className="open-work-types-value">{euros(cell.value)}</span>
        <span className="open-work-types-detail">
          {tr('Offers', { count: cell.offerIds.size })}
        </span>
        <span className="open-work-types-detail">
          {quantitiesText(cell.quantities)}
        </span>
      </>
    );

  const isChosen = (rowGroup: null | string, column: null | string) =>
    group === rowGroup && department === column;

  return (
    <PageContainer fullWidth>
      <div className="offer-dashboard">
        {tabs}
        <div className="offer-dashboard-header">
          <div>
            <h1>{tr('Title')}</h1>
            <p className="offer-dashboard-subtitle">{tr('Subtitle')}</p>
          </div>
          <div className="offer-dashboard-filters">
            {/* The currency of the sums, with the exchange rate below it */}
            <CurrencyToggle
              base={report.currency}
              onChange={report.setCurrency}
              rate={report.rate}
              value={report.currency}
            />
            {/* The offers made during the last months, by their latest revision */}
            <div className="offer-dashboard-segments" role="group">
              {[...PERIOD_MONTHS, null].map((key) => (
                <button
                  className={key === months ? 'active' : ''}
                  key={key ?? 'all'}
                  onClick={() => setMonths(key)}
                  type="button"
                >
                  {key === null
                    ? tr('All periods')
                    : t(`${translations.offers}:dashboard.Months`, {
                        count: key,
                      })}
                </button>
              ))}
            </div>
            <div className="offer-dashboard-segments" role="group">
              {TYPE_FILTERS.map((key) => (
                <button
                  className={key === typeFilter ? 'active' : ''}
                  key={key}
                  onClick={() => setTypeFilter(key)}
                  type="button"
                >
                  {tr(`types.${key}`)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {failed ? <p>{tr('Load failed')}</p> : null}
        {data === null && !failed ? <p>{tr('Loading')}</p> : null}

        {data !== null ? (
          <>
            <section className="offer-dashboard-card">
              <h2>{tr('By department')}</h2>
              <p className="offer-dashboard-card-info">
                {tr('Table info')}
                {data.restrictedCount > 0
                  ? ` ${tr('Restricted', { count: data.restrictedCount })}`
                  : ''}
              </p>
              {groups.length === 0 ? (
                <p>{tr('No open offers')}</p>
              ) : (
                <div className="offer-dashboard-table-wrapper">
                  <table className="offer-dashboard-table open-work-types-table">
                    <thead>
                      <tr>
                        <th>{tr('Work type')}</th>
                        {departments.map((key) => (
                          <th className="numeric" key={key}>
                            {key || tr('No department')}
                          </th>
                        ))}
                        <th className="numeric total">{tr('Total')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groups.map((rowGroup) => (
                        <tr key={rowGroup}>
                          <td
                            className={`target ${
                              isChosen(rowGroup, null) ? 'chosen' : ''
                            }`}
                            onClick={() => choose(rowGroup, null)}
                          >
                            {groupLabel(rowGroup)}
                          </td>
                          {departments.map((column) => (
                            <td
                              className={`numeric ${
                                isChosen(rowGroup, column) ? 'chosen' : ''
                              }`}
                              key={column}
                              onClick={() => choose(rowGroup, column)}
                            >
                              {cellContent(cells.get(`${rowGroup}|${column}`))}
                            </td>
                          ))}
                          <td
                            className={`numeric total ${
                              isChosen(rowGroup, null) ? 'chosen' : ''
                            }`}
                            onClick={() => choose(rowGroup, null)}
                          >
                            {cellContent(groupTotals.get(rowGroup))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td
                          className={`target ${
                            isChosen(null, null) ? 'chosen' : ''
                          }`}
                          onClick={() => choose(null, null)}
                        >
                          {tr('Total')}
                        </td>
                        {departments.map((column) => (
                          <td
                            className={`numeric ${
                              isChosen(null, column) ? 'chosen' : ''
                            }`}
                            key={column}
                            onClick={() => choose(null, column)}
                          >
                            <span className="open-work-types-value">
                              {euros(departmentTotals.get(column)?.value ?? 0)}
                            </span>
                            <span className="open-work-types-detail">
                              {tr('Offers', {
                                count:
                                  departmentTotals.get(column)?.offerIds.size ??
                                  0,
                              })}
                            </span>
                          </td>
                        ))}
                        <td className="numeric total">
                          <span className="open-work-types-value">
                            {euros(total.value)}
                          </span>
                          <span className="open-work-types-detail">
                            {tr('Offers', { count: total.offerIds.size })}
                          </span>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </section>

            {/* The positions of the same name summed by department */}
            <section className="offer-dashboard-card offer-dashboard-table-card">
              <h2>{tr('By name')}</h2>
              <p className="offer-dashboard-card-info">{tr('By name info')}</p>
              {names.length === 0 ? (
                <p>{tr('No open offers')}</p>
              ) : (
                <>
                  <BarList
                    bars={names.slice(0, NAMES_CHARTED).map((summary) => ({
                      color: PALETTE[0],
                      key: summary.key,
                      label: summary.label,
                      parts: nameDepartments.map((key) => ({
                        color: departmentColor(key),
                        label: `${departmentLabel(key)}: ${euros(
                          summary.byDepartment.get(key)?.value ?? 0
                        )}`,
                        value: summary.byDepartment.get(key)?.value ?? 0,
                      })),
                      text: euros(summary.total.value),
                      value: summary.total.value,
                    }))}
                    onSelect={chooseName}
                    selected={positionName}
                  />
                  <Legend
                    series={nameDepartments.map((key) => ({
                      color: departmentColor(key),
                      key,
                      label: departmentLabel(key),
                    }))}
                  />
                  <div className="offer-dashboard-table-wrapper">
                    <table className="offer-dashboard-table open-work-types-table">
                      <thead>
                        <tr>
                          <th>{tr('Name')}</th>
                          {nameDepartments.map((key) => (
                            <th className="numeric" key={key}>
                              {departmentLabel(key)}
                            </th>
                          ))}
                          <th className="numeric total">{tr('Total')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shownNames.map((summary) => (
                          <tr
                            className={
                              positionName === summary.key ? 'chosen' : ''
                            }
                            key={summary.key}
                            onClick={() => chooseName(summary.key)}
                          >
                            <td
                              className={`target ${
                                positionName === summary.key ? 'chosen' : ''
                              }`}
                            >
                              {summary.label}
                            </td>
                            {nameDepartments.map((key) => (
                              <td className="numeric" key={key}>
                                {cellContent(summary.byDepartment.get(key))}
                              </td>
                            ))}
                            <td className="numeric total">
                              {cellContent(summary.total)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {names.length > NAMES_SHOWN ? (
                    <button
                      className="offer-dashboard-show-all"
                      onClick={() => setShowAllNames(!showAllNames)}
                      type="button"
                    >
                      {showAllNames
                        ? t(`${translations.offers}:dashboard.Show less`)
                        : t(`${translations.offers}:dashboard.Show all`, {
                            count: names.length,
                          })}
                    </button>
                  ) : null}
                </>
              )}
            </section>

            <section className="offer-dashboard-card offer-dashboard-table-card">
              <h2>
                {tr('Positions', { count: chosenPositions.length })}
                {group !== null ||
                department !== null ||
                positionName !== null ? (
                  <button
                    className="open-work-types-filter"
                    onClick={() => choose(null, null)}
                    type="button"
                  >
                    {[
                      group === null ? null : groupLabel(group),
                      department === null
                        ? null
                        : department || tr('No department'),
                      positionName === null
                        ? null
                        : names.find((summary) => summary.key === positionName)
                            ?.label ?? positionName,
                    ]
                      .filter((label) => label !== null)
                      .join(' · ')}
                    {' ×'}
                  </button>
                ) : null}
              </h2>
              <div className="offer-dashboard-table-wrapper">
                <table className="offer-dashboard-table">
                  <thead>
                    <tr>
                      <th>{tr('Work type')}</th>
                      <th>{tr('Offer')}</th>
                      <th>{tr('Department')}</th>
                      <th>{tr('Status')}</th>
                      <th>{tr('Position')}</th>
                      <th>{tr('Name')}</th>
                      <th className="numeric">{tr('Quantity')}</th>
                      <th className="numeric">{tr('Sales')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shownPositions.map((position) => {
                      const offer = offersById.get(position.offerId);

                      return (
                        <tr
                          key={position.id}
                          onClick={() =>
                            offer
                              ? history.push(
                                  `/offers/${offer.id}/${offer.type}`
                                )
                              : undefined
                          }
                        >
                          <td>{groupLabel(position.group)}</td>
                          <td className="target">
                            {offer?.targetName}
                            <span className="open-work-types-type">
                              {offer
                                ? t(
                                    `${translations.offers}:phases.${offer.type}`
                                  )
                                : ''}
                            </span>
                          </td>
                          <td>{departmentOf(offer) || tr('No department')}</td>
                          <td>
                            {offer
                              ? getOfferStatusLabel(t, offer.status, offer.type)
                              : ''}
                          </td>
                          <td>{position.code}</td>
                          <td>{position.name}</td>
                          <td className="numeric">
                            {`${formatGroupedNumber(
                              position.quantity,
                              2,
                              0
                            )} ${unitLabel(position.unit ?? '')}`.trim()}
                          </td>
                          <td className="numeric">{euros(position.value)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {chosenPositions.length > POSITIONS_SHOWN ? (
                <button
                  className="offer-dashboard-show-all"
                  onClick={() => setShowAll(!showAll)}
                  type="button"
                >
                  {showAll
                    ? t(`${translations.offers}:dashboard.Show less`)
                    : t(`${translations.offers}:dashboard.Show all`, {
                        count: chosenPositions.length,
                      })}
                </button>
              ) : null}
            </section>
          </>
        ) : null}
      </div>
    </PageContainer>
  );
};

export default OpenWorkTypes;
